
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Divider, Checkbox, Chip, CircularProgress, Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { getServiceLines, addServiceLine, updateServiceLine, deleteServiceLine, getServiceLineMapping, updateServiceLineMapping, getCostCenters } from '../api/api';

const ServiceLineSetupPage = () => {
    const [serviceLines, setServiceLines] = useState([]);
    const [selectedLine, setSelectedLine] = useState(null);
    const [costCenters, setCostCenters] = useState([]);
    const [mapping, setMapping] = useState({}); // { service_line_id: [cost_centers] }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Modal State
    const [openModal, setOpenModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [linesRes, ccRes, mapRes] = await Promise.all([
                getServiceLines(),
                getCostCenters(),
                getServiceLineMapping()
            ]);

            const lines = Array.isArray(linesRes?.data) ? linesRes.data : [];
            const ccs = Array.isArray(ccRes?.data) ? ccRes.data : [];
            const maps = Array.isArray(mapRes?.data) ? mapRes.data : [];

            setServiceLines(lines);
            setCostCenters(ccs);

            // Process mapping into a more usable format
            const mapData = {};
            maps.forEach(m => {
                if (m && m.service_line_id) {
                    if (!mapData[m.service_line_id]) mapData[m.service_line_id] = [];
                    mapData[m.service_line_id].push(m.cost_center);
                }
            });
            setMapping(mapData);

        } catch (err) {
            console.error(err);
            setError('Erro ao carregar dados. Verifique a conexão.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectLine = (line) => {
        setSelectedLine(line);
    };

    const handleOpenModal = (line = null) => {
        if (line) {
            setEditMode(true);
            setFormData({ name: line.name, description: line.description });
            setSelectedLine(line); // Ensure selected for update context
        } else {
            setEditMode(false);
            setFormData({ name: '', description: '' });
        }
        setOpenModal(true);
    };

    const handleSubmit = async () => {
        try {
            if (editMode) {
                await updateServiceLine(selectedLine.id, formData);
            } else {
                await addServiceLine(formData);
            }
            setOpenModal(false);
            loadData();
        } catch (err) {
            alert('Erro ao salvar linha de serviço.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta linha de serviço?')) {
            try {
                await deleteServiceLine(id);
                if (selectedLine?.id === id) setSelectedLine(null);
                loadData();
            } catch (err) {
                alert('Erro ao excluir.');
            }
        }
    };

    const handleToggleCostCenter = (cc) => {
        if (!selectedLine) return;

        const currentMapping = mapping[selectedLine.id] || [];
        const newMapping = currentMapping.includes(cc)
            ? currentMapping.filter(c => c !== cc)
            : [...currentMapping, cc];

        setMapping({ ...mapping, [selectedLine.id]: newMapping });
    };

    const handleSaveMapping = async () => {
        if (!selectedLine) return;
        try {
            const centers = mapping[selectedLine.id] || [];
            await updateServiceLineMapping(selectedLine.id, centers);
            alert('Mapeamento salvo com sucesso!');
            loadData(); // Reload to refresh mapping state from server
        } catch (err) {
            alert('Erro ao salvar mapeamento.');
        }
    };

    // Filter cost centers checking if they are assigned to OTHER service lines
    const isAssignedToOther = (cc) => {
        for (const [lineId, centers] of Object.entries(mapping)) {
            if (parseInt(lineId) !== selectedLine?.id && centers.includes(cc)) {
                return true;
            }
        }
        return false;
    };

    const getAssignedLineName = (cc) => {
        for (const [lineId, centers] of Object.entries(mapping)) {
            if (centers.includes(cc)) {
                const line = serviceLines.find(l => l.id === parseInt(lineId));
                return line ? line.name : 'Desconhecido';
            }
        }
        return null;
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>Configuração de Linhas de Serviço</Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
                Defina as linhas de serviço (ex: UTI, Internação, Ambulatório) e associe aos centros de custo.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <CircularProgress />}

            <Grid container spacing={3}>
                {/* Left Panel: Service Lines List */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Linhas de Serviço</Typography>
                            <IconButton color="primary" onClick={() => handleOpenModal()}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                        <Divider />
                        <List>
                            {serviceLines.map((line) => (
                                <ListItem
                                    button
                                    key={line.id}
                                    selected={selectedLine?.id === line.id}
                                    onClick={() => handleSelectLine(line)}
                                >
                                    <ListItemText
                                        primary={line.name}
                                        secondary={`${(mapping[line.id] || []).length} centros associados`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" size="small" onClick={() => handleOpenModal(line)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton edge="end" size="small" onClick={() => handleDelete(line.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                            {serviceLines.length === 0 && <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>Nenhuma linha cadastrada.</Typography>}
                        </List>
                    </Paper>
                </Grid>

                {/* Right Panel: Mapping */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        {selectedLine ? (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        Mapeamento: {selectedLine.name}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveMapping}
                                    >
                                        Salvar Mapeamento
                                    </Button>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>Selecione os Centros de Custo:</Typography>
                                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                    <List dense>
                                        {costCenters.map((cc) => {
                                            const isChecked = (mapping[selectedLine.id] || []).includes(cc);
                                            const otherLine = isAssignedToOther(cc);
                                            const assignedName = getAssignedLineName(cc);

                                            return (
                                                <ListItem key={cc} button onClick={() => handleToggleCostCenter(cc)}>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={isChecked}
                                                        tabIndex={-1}
                                                        disableRipple
                                                    />
                                                    <ListItemText
                                                        primary={cc}
                                                        secondary={otherLine && !isChecked ? `Atribuído a: ${assignedName}` : null}
                                                    />
                                                    {otherLine && !isChecked && (
                                                        <Chip label="Em uso" size="small" color="warning" variant="outlined" />
                                                    )}
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            </>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <Typography variant="body1" color="textSecondary">
                                    Selecione uma linha de serviço à esquerda para editar o mapeamento.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Add/Edit Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editMode ? 'Editar Linha de Serviço' : 'Nova Linha de Serviço'}</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <TextField
                            label="Nome"
                            fullWidth
                            variant="outlined"
                            value={formData.name || ''}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                            autoFocus
                        />
                        <TextField
                            label="Descrição"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={formData.description || ''}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ServiceLineSetupPage;
